import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/expenseAccount.query"

import type {
  GetInvoiceExpenseAccountOptionsQuery,
  GetInvoiceExpenseAccountOptionsQueryVariables,
} from "@/graphql/codegen/graphql"
import { GetInvoiceExpenseAccountOptionsDocument, GlAccountType } from "@/graphql/codegen/graphql"
import { formatGlAccountLabel } from "@/screens/Accounting/subfeatures/ChartOfAccounts/utils/formatGlAccountLabel"

export interface ExpenseAccountOption {
  id: string
  label: string
}

interface UseExpenseAccountOptionsReturn {
  expenseAccounts: ExpenseAccountOption[]
  isFetching: boolean
}

const EXPENSE_ACCOUNTS_LIMIT = 200

export const useExpenseAccountOptions = (): UseExpenseAccountOptionsReturn => {
  const [{ data, fetching }] = useQuery<
    GetInvoiceExpenseAccountOptionsQuery,
    GetInvoiceExpenseAccountOptionsQueryVariables
  >({
    query: GetInvoiceExpenseAccountOptionsDocument,
    variables: {
      input: {
        filter: {
          type: [GlAccountType.Expense],
          isActive: true,
        },
        offset: 0,
        limit: EXPENSE_ACCOUNTS_LIMIT,
      },
    },
  })

  const expenseAccounts = useMemo<ExpenseAccountOption[]>(() => {
    if (data?.glAccount.list.__typename !== "GlAccountListSuccess") return []

    return data.glAccount.list.glAccounts
      .filter((glAccount): glAccount is NonNullable<typeof glAccount> => glAccount !== null)
      .map((glAccount) => ({
        id: glAccount.glAccountId ?? "",
        label: formatGlAccountLabel({
          number: glAccount.number,
          subNumber: glAccount.subNumber,
          title: glAccount.title,
        }),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [data])

  return { expenseAccounts, isFetching: fetching }
}
